import { Alert, Button, Divider, Input, Modal, Popconfirm, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  CustomerApiKey,
  IssuedCustomerApiKey,
} from '../../api/customerApiKeysApi';
import {
  ApiError,
  ApiRequest,
  useApiRequests,
  useApiResource,
} from '../../hooks/useApi';
import { useApiSettings } from '../../hooks/useApi/useApiSettings';
import { DateFormat, useI18n } from '../../hooks/useI18n';
import { messages } from '../../messages';
import { ApiErrorDetails } from '../apiErrorDetails/apiErrorDetails';
import { Columns, Table } from '../table';

export function CustomerApiKeys({
  resourceKey,
  issueApiKey,
  getApiKeys,
  revokeApiKey,
  reactivateApiKey,
  onSelectApiKey,
}: {
  resourceKey: number;
  issueApiKey: () => ApiRequest<IssuedCustomerApiKey>;
  getApiKeys: () => ApiRequest<CustomerApiKey[]>;
  revokeApiKey: (apiKeyId: number) => ApiRequest;
  reactivateApiKey: (apiKeyId: number) => ApiRequest;
  onSelectApiKey?: (apiKeyId: number) => void;
}) {
  const { t, formatDate } = useI18n();
  const apiSettings = useApiSettings();
  const issueApiKeyRequest = useApiRequests(issueApiKey);
  const revokeApiKeyRequest = useApiRequests(revokeApiKey);
  const reactivateApiKeyRequest = useApiRequests(reactivateApiKey);
  const apiKeys = useApiResource(getApiKeys, {
    emptyValue: [],
    clearDataOnLoad: false,
  });
  const [apiKey, setApiKey] = useState<string>();
  const [error, setError] = useState<ApiError>();

  useEffect(() => {
    apiKeys.refresh();
  }, [resourceKey]);

  const issue = () => {
    setError(undefined);
    issueApiKeyRequest
      .create({
        onSuccess: (result) => {
          setApiKey(result.apiKey);
          apiKeys.refresh();
        },
        onError: setError,
      })
      .send();
  };

  const revoke = (apiKeyId: number) => {
    setError(undefined);
    revokeApiKeyRequest
      .update({
        onSuccess: () => apiKeys.refresh(),
        onError: setError,
      })
      .send(apiKeyId);
  };

  const reactivate = (apiKeyId: number) => {
    setError(undefined);
    reactivateApiKeyRequest
      .update({
        onSuccess: () => apiKeys.refresh(),
        onError: setError,
      })
      .send(apiKeyId);
  };

  const columns: Columns<CustomerApiKey> = [
    {
      header: t(messages.customer.apiKeyId),
      accessor: 'id',
      width: 90,
      cell: ({ value }) =>
        onSelectApiKey ? (
          <Button type="link" size="small" onClick={() => onSelectApiKey(value)}>
            #{value}
          </Button>
        ) : (
          `#${value}`
        ),
    },
    {
      header: t(messages.customer.apiKeyIssuedBy),
      accessor: 'issuedByUserName',
    },
    {
      header: t(messages.customer.apiKeyIssuedAt),
      accessor: 'createdAt',
      cell: ({ value }) => formatDate(value, DateFormat.DATETIME),
    },
    {
      header: t(messages.shared.status),
      accessor: 'revokedAt',
      width: 100,
      cell: ({ row }) =>
        row.revokedAt ? (
          <Tag color="red">{t(messages.customer.apiKeyRevoked)}</Tag>
        ) : (
          <Tag color="green">{t(messages.customer.apiKeyActive)}</Tag>
        ),
    },
    {
      header: '',
      width: 100,
      cell: ({ row }) =>
        row.revokedAt ? (
          <Popconfirm
            title={t(messages.customer.reactivateApiKeyConfirmation)}
            onConfirm={() => reactivate(row.id)}
          >
            <Button
              type="primary"
              size="small"
              loading={reactivateApiKeyRequest.isLoading}
            >
              {t(messages.customer.reactivateApiKey)}
            </Button>
          </Popconfirm>
        ) : (
          <Popconfirm
            title={t(messages.customer.revokeApiKeyConfirmation)}
            onConfirm={() => revoke(row.id)}
          >
            <Button
              type="danger"
              size="small"
              loading={revokeApiKeyRequest.isLoading}
            >
              {t(messages.customer.revokeApiKey)}
            </Button>
          </Popconfirm>
        ),
    },
  ];
  const apiError = error ?? apiKeys.error;

  return (
    <>
      {apiError && <ApiErrorDetails error={apiError} />}
      <Button
        type="primary"
        size="small"
        loading={issueApiKeyRequest.isLoading}
        onClick={issue}
      >
        {t(messages.customer.issueApiKey)}
      </Button>
      <Button
        size="small"
        icon="api"
        style={{ marginLeft: 8 }}
        onClick={() =>
          window.open(
            apiSettings.customerApiSwaggerUri,
            '_blank',
            'noopener,noreferrer',
          )
        }
      >
        {t(messages.customer.openApiSwagger)}
      </Button>
      <Divider />
      <Table<CustomerApiKey>
        columns={columns}
        items={apiKeys.data}
        isLoading={apiKeys.isLoading}
      />
      <Modal
        title={t(messages.customer.apiKeyIssued)}
        visible={apiKey !== undefined}
        closable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText={t(messages.customer.closeApiKey)}
        onOk={() => setApiKey(undefined)}
      >
        <Alert
          type="warning"
          showIcon={true}
          message={t(messages.customer.apiKeyIssuedDescription)}
          style={{ marginBottom: 15 }}
        />
        <Input.TextArea
          readOnly={true}
          rows={3}
          value={apiKey}
          onFocus={(event) => event.currentTarget.select()}
        />
      </Modal>
    </>
  );
}
